import React from 'react';
import { DropZone } from './DropZone';
import { FileItem } from './FileItem';
import { ImagePreview } from './ImagePreview';
import styles from './styles.module.scss';

/**
 * アップロード済みファイルの情報
 */
type UploadedFile = {
  /** ファイル名（表示用） */
  name: string;
  /** ファイルサイズ（バイト）（表示用） */
  sizeBytes: number;
  /** 新規ファイルの実体。Orval fetcher（Blob 型）にそのまま渡す用 */
  file?: File;
  /** 既存ファイルのURL。編集画面で初期値として渡す場合に設定する */
  url?: string;
  /** 画像プレビュー用 URL（type='image' のみ使用）。URL.createObjectURL(file) または url */
  preview?: string;
};

/**
 * 既存画像ファイル（URLから取得したもの）
 * type='image' の defaultFile に使用する
 */
type DefaultImageFile = { url: string };

/**
 * 既存添付ファイル（ファイル名のみ保持）
 * type='file' の defaultFiles に使用する
 */
type DefaultAttachmentFile = { name: string };

/**
 * 既存ファイル（URLから取得したもの）の型
 * - DefaultImageFile: type='image' の初期値として使用（url でプレビュー表示）
 * - DefaultAttachmentFile: type='file' の既存ファイル一覧として使用（name のみ表示）
 */
type DefaultFile = DefaultImageFile | DefaultAttachmentFile;

type Props = {
  type: 'image' | 'file';
  value: UploadedFile[];
  isDragging: boolean;
  /** コンポーネント内部バリデーションエラー（useFileUpload から渡す） */
  validationError?: string;
  /** フォームバリデーションエラー（ReactHookForm 等から渡す） */
  error?: string;
  /** input[accept] に渡す文字列（例: ".jpg,.png,.pdf"） */
  acceptAttr: string;
  /** アップロード中フラグ。trueのとき DropZone をLoading状態で表示する */
  isUploading?: boolean;
  /** disabled フラグ。trueのとき DropZone を非活性状態で表示する */
  disabled?: boolean;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: (index: number) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  /**
   * 既存ファイル一覧（URLから取得したもの）。type='file' のみ使用。
   * 新規追加ファイル（value）とは別にリスト表示される
   */
  defaultFiles?: DefaultFile[];
  /**
   * 既存ファイルの削除ボタン押下時のコールバック。type='file' のみ使用。
   * 引数は defaultFiles の index
   */
  onDefaultFileRemove?: (index: number) => void;
  /**
   * 複数ファイル選択モードか否か。false（single）のとき、ファイル選択済みなら DropZone を非表示にする
   */
  isMultiple?: boolean;
};

export const FileUploadPresentation: React.FC<Props> = ({
  type,
  value,
  isDragging,
  validationError,
  error,
  acceptAttr,
  isUploading = false,
  disabled = false,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileInputChange,
  onRemove,
  fileInputRef,
  defaultFiles,
  onDefaultFileRemove,
  isMultiple,
}) => (
  <div className={styles.wrapper}>
    {/* type='image': 選択済みならプレビュー表示、未選択なら DropZone 表示 */}
    {type === 'image' && value.length > 0 ? (
      <ImagePreview
        src={value[0].preview ?? value[0].url ?? ''}
        alt={value[0].name}
        onRemove={() => onRemove(0)}
      />
    ) : type === 'image' ? (
      <DropZone
        isDragging={isDragging}
        acceptAttr={acceptAttr}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onFileInputChange={onFileInputChange}
        fileInputRef={fileInputRef}
        isUploading={isUploading}
        disabled={disabled}
        isError={!!error || !!validationError}
      />
    ) : null}

    {/* type='file': DropZone + 下にファイルリスト。single モードでファイル選択済みのときは DropZone を非表示 */}
    {type === 'file' && (
      <>
        {(!isMultiple ? value.length === 0 : true) && (
          <DropZone
            isDragging={isDragging}
            acceptAttr={acceptAttr}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onFileInputChange={onFileInputChange}
            fileInputRef={fileInputRef}
            isUploading={isUploading}
            disabled={disabled}
            isError={!!error || !!validationError}
          />
        )}
        {(value.length > 0 || (defaultFiles != null && defaultFiles.length > 0)) && (
          <ul className={styles.fileList}>
            {defaultFiles?.map((file, index) => (
              // 既存ファイル: 新規ファイルより上に表示する
              <li key={`default-${'name' in file ? file.name : ''}-${index}`}>
                <FileItem
                  name={'name' in file ? file.name : ''}
                  disabled={isUploading}
                  onRemove={() => onDefaultFileRemove?.(index)}
                />
              </li>
            ))}
            {value.map((file, index) => (
              // ファイル名とインデックスの組み合わせをキーに使用
              <li key={`${file.name}-${index}`}>
                <FileItem
                  name={file.name}
                  sizeBytes={file.sizeBytes}
                  disabled={isUploading}
                  onRemove={() => onRemove(index)}
                />
              </li>
            ))}
          </ul>
        )}
      </>
    )}

    {/* コンポーネント内部バリデーションエラー（ファイルサイズ超過・非対応拡張子等） */}
    {validationError && <p className={styles.error}>{validationError}</p>}
    {/* フォームバリデーションエラー（ReactHookForm 等から渡される） */}
    {error && <p className={styles.error}>{error}</p>}
  </div>
);
